import {
  UseMutationResult,
  UseQueryResult,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { FC, ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { TenantContext } from "./TenantContext";

import { ROUTES } from "@/constants/routes";
import { instance } from "@/lib/axios/instance";
import { useAuth } from "@/lib/hooks/useAuth";
import useToast from "@/lib/hooks/useToast";
import { queryClient } from "@/main";
import { AddTenantRequest } from "@/types/request/AddTenantRequest";
import { UpdateTenantSettingsRequest } from "@/types/request/UpdateTenantSettingsRequest";
import { AddTenantResponse } from "@/types/response/AddTenantResponse";
import { GetTenantsResponse } from "@/types/response/GetTenantsResponse";
import { TenantType } from "@/types/tenant/TenantType";

interface TenantProviderProps {
  children: ReactNode;
  page?: number;
}

export const TenantProvider: FC<TenantProviderProps> = ({
  children,
  page,
}): ReactElement<TenantProviderProps> => {
  const { role, tenantId, setTenantId } = useAuth();
  const { showToast } = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    data: tenants,
    isLoading: tenantsLoading,
    isFetching: tenantsFetching,
  }: UseQueryResult<GetTenantsResponse, AxiosError> = useQuery({
    queryKey: ["tenants", page ?? 1],
    queryFn: async () => {
      const response = await instance.get<GetTenantsResponse>(
        `/api/v2/Tenants`,
        {
          params: { page: page ?? 1, pageSize: 150 },
        },
      );
      if (!response.data) {
        return { page: page ?? 1, totalRecords: 0, data: [] };
      }
      return response.data;
    },
    refetchOnReconnect: false,
    staleTime: 20 * 60 * 1000,
    enabled: role === "SuperAdmin",
  });

  const {
    data: tenant,
    isLoading: tenantLoading,
  }: UseQueryResult<TenantType, AxiosError> = useQuery({
    queryKey: ["tenant", tenantId],
    queryFn: async () => {
      const res = await instance.get<TenantType>(
        `/api/v2/Tenants/${tenantId}`
      );
      if (res.data === undefined) {
        throw new Error("Tenant response had no data");
      }
      return res.data;
    },
    refetchOnReconnect: false,
    staleTime: 20 * 60 * 1000,
    enabled: !!tenantId,
  });

  const addTenant: UseMutationResult<
    AxiosResponse<AddTenantResponse>,
    AxiosError,
    AddTenantRequest
  > = useMutation({
    mutationFn: (AddTenantRequest: AddTenantRequest) =>
      instance.post("/api/v2/Tenants", AddTenantRequest),
    onSuccess: async (response) => {
      const tentantId = response.data.id;
      await queryClient.invalidateQueries({ queryKey: ["tenants"] });
      setTenantId(tentantId);
      navigate(`/${ROUTES.clients}`, {
        replace: true,
      });
      showToast(
        t("superAdmin.tenant.validationMessageDesc"),
        "success",
        {},
        t("superAdmin.tenant.validationMessage"),
      );
    },
  });

  const updateTenantSettings: UseMutationResult<
    AxiosResponse<AddTenantResponse>,
    AxiosError,
    UpdateTenantSettingsRequest
  > = useMutation({
    mutationFn: (request: UpdateTenantSettingsRequest) =>
      instance.put(`/api/v2/Tenants/${request.id}/settings`, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
    },
  });

  return (
    <TenantContext.Provider
      value={{
        addTenant,
        tenant,
        tenantLoading,
        tenants,
        tenantsFetching,
        tenantsLoading,
        updateTenantSettings: updateTenantSettings,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};
